import React, { useState, useMemo } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useProjects } from '../hooks/useProjects';
import { useTasks } from '../hooks/useTasks';
import { useFinance } from '../hooks/useFinance';
import ProjectCard from '../components/projects/ProjectCard';
import ProjectModal from '../components/projects/ProjectModal';
import TaskModal from '../components/tasks/TaskModal';
import TaskCard from '../components/tasks/TaskCard';
import TaskDetailModal from '../components/tasks/TaskDetailModal';
import EmptyState from '../components/ui/EmptyState';

const STATUS_TABS = ['all', 'active', 'paused', 'completed'];
const STATUS_LABELS = {
  all: 'Todos',
  active: 'Activos',
  paused: 'Pausados',
  completed: 'Completados'
};

export default function Projects() {
  const { projects, loading, addProject, updateProject, deleteProject } = useProjects();
  const { tasks, addTask, updateTask, deleteTask, toggleTask } = useTasks();
  const { addTransaction, allCategories } = useFinance();

  const [showProjectModal, setShowProjectModal] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [detailTask, setDetailTask] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  const filteredProjects = useMemo(() =>
    projects.filter(p => statusFilter === 'all' || p.status === statusFilter),
    [projects, statusFilter]
  );

  const projectTasks = useMemo(() =>
    selectedProject ? tasks.filter(t => t.projectId === selectedProject.id) : [],
    [tasks, selectedProject]
  );

  const totalRevenue = projects
    .filter(p => p.status === 'completed')
    .reduce((a, p) => a + (p.budget || 0), 0);

  const pendingRevenue = projects
    .filter(p => p.status === 'active')
    .reduce((a, p) => a + (p.budget || 0), 0);

  // eslint-disable-next-line no-unused-vars
  const projectProgress = useMemo(() => {
    return projects.reduce((acc, project) => {
      const linkedTasks = tasks.filter(task => task.projectId === project.id);
      const completed = linkedTasks.filter(task => task.completed).length;
      acc[project.id] = linkedTasks.length ? Math.round((completed / linkedTasks.length) * 100) : (project.progress || 0);
      return acc;
    }, {});
  }, [projects, tasks]);

  const handleSaveProject = (data) => {
    if (editProject) {
      updateProject(editProject.id, data);
      if (selectedProject?.id === editProject.id) {
        setSelectedProject({ ...editProject, ...data });
      }
    } else {
      addProject(data);
    }
    setEditProject(null);
  };

  const handleEditProject = (p) => {
    setEditProject(p);
    setShowProjectModal(true);
  };

  const handleSaveTask = (data) => {
    if (editTask) {
      updateTask(editTask.id, data);
    } else {
      addTask({
        ...data,
        projectId: selectedProject.id,
        type: 'proyecto'
      });
    }
    setEditTask(null);
  };

  const handleCreateExpenseFromTask = async (task, expense) => {
    const transactionRef = await addTransaction({
      amount: expense.amount,
      category: expense.category,
      description: expense.description || task.title,
      date: new Date().toISOString().slice(0, 10),
      type: 'expense',
      taskId: task.id,
      projectId: task.projectId || selectedProject?.id || null,
      source: 'task',
    });

    await updateTask(task.id, {
      expenseId: transactionRef.id,
      expenseAmount: expense.amount,
    });
  };

  return (
    <div className="animate-fade-in">

      {/* HEADER */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display font-bold text-white">Proyectos</h1>
          <p className="text-slate-500 text-sm mt-0.5">Modo negocios</p>
        </div>

        <button
          onClick={() => {
            setEditProject(null);
            setShowProjectModal(true);
          }}
          className="btn-primary"
        >
          + Nuevo proyecto
        </button>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {[
          { label: 'Proyectos activos', val: projects.filter(p => p.status === 'active').length, color: '#10b981' },
          { label: 'Ingresos cobrados', val: `$${totalRevenue.toLocaleString()}`, color: '#6366f1' },
          { label: 'Ingresos pendientes', val: `$${pendingRevenue.toLocaleString()}`, color: '#f59e0b' },
        ].map(s => (
          <div key={s.label} className="card p-4 text-center">
            <p className="text-xl font-display font-bold" style={{ color: s.color }}>
              {s.val}
            </p>
            <p className="text-xs text-slate-500 mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* FILTER TABS */}
      <div className="flex gap-2 mb-5">
        {STATUS_TABS.map(tab => (
          <button
            key={tab}
            onClick={() => setStatusFilter(tab)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
              statusFilter === tab
                ? ''
                : 'bg-surface-card text-slate-400 hover:text-white border-surface-border'
            }`}
            style={statusFilter === tab ? { backgroundColor: 'var(--accent)', color: 'var(--text-on-accent)', borderColor: 'var(--accent)' } : {}}
          >
            {STATUS_LABELS[tab]}
          </button>
        ))}
      </div>

      {/* PROJECTS GRID */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">

        {loading ? (
          <p className="text-slate-500 col-span-3 text-center py-8">Cargando...</p>
        ) : filteredProjects.length === 0 ? (
          <div className="col-span-3">
            <EmptyState
              iconClass="bi-rocket-takeoff"
              title={statusFilter === 'all' ? 'Todavia no hay proyectos' : `Sin proyectos ${STATUS_LABELS[statusFilter].toLowerCase()}`}
              description={statusFilter === 'all'
                ? 'Crea tu primer proyecto para agrupar tareas, clientes e ingresos desde un solo lugar.'
                : 'Cambia el filtro o crea un nuevo proyecto cuando quieras mover algo a esta etapa.'}
              actionLabel="Nuevo proyecto"
              onAction={() => {
                setEditProject(null);
                setShowProjectModal(true);
              }}
            />
          </div>
        ) : (
          <AnimatePresence initial={false}>
            {filteredProjects.map(p => (
              <ProjectCard
                key={p.id}
                project={p}
                onSelect={(proj) => setSelectedProject(proj)}
                onEdit={(proj) => handleEditProject(proj)}
                onDelete={deleteProject}
              />
            ))}
          </AnimatePresence>
        )}

      </div>

      {/* PROJECT DETAIL */}
      {selectedProject && (
        <div className="card p-5 animate-slide-up">

          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-display font-bold text-white">
                {selectedProject.name}
              </h2>
              <p className="text-xs text-slate-500">
                {selectedProject.client}
              </p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => {
                  setEditTask(null);
                  setShowTaskModal(true);
                }}
                className="btn-primary text-sm py-1.5 px-3"
              >
                + Tarea
              </button>

              <button
                onClick={() => setSelectedProject(null)}
                className="btn-ghost text-sm py-1.5 px-3"
              >
                Cerrar
              </button>
            </div>
          </div>

          {projectTasks.length === 0 ? (
            <EmptyState
              iconClass="bi-list-check"
              title="Proyecto sin tareas"
              description="Agrega el siguiente paso para que el progreso de este proyecto empiece a moverse."
              actionLabel="Agregar tarea"
              onAction={() => {
                setEditTask(null);
                setShowTaskModal(true);
              }}
            />
          ) : (
            <div className="space-y-2">
              <AnimatePresence initial={false}>
                {projectTasks.map(task => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    onToggle={toggleTask}
                    onClick={setDetailTask}
                    onEdit={(t) => {
                      setEditTask(t);
                      setShowTaskModal(true);
                    }}
                    onDelete={deleteTask}
                  />
                ))}
              </AnimatePresence>
            </div>
          )}

        </div>
      )}

      {/* MODALS */}
      {showProjectModal && (
        <ProjectModal
          project={editProject}
          onSave={handleSaveProject}
          onClose={() => {
            setShowProjectModal(false);
            setEditProject(null);
          }}
        />
      )}

      {showTaskModal && (
        <TaskModal
          task={editTask}
          onSave={handleSaveTask}
          onClose={() => {
            setShowTaskModal(false);
            setEditTask(null);
          }}
        />
      )}

      {detailTask && (
        <TaskDetailModal
          task={detailTask}
          onClose={() => setDetailTask(null)}
          onEdit={(t) => {
            setEditTask(t);
            setShowTaskModal(true);
          }}
          onDelete={(id) => {
            deleteTask(id);
            setDetailTask(null);
          }}
          onCreateExpense={handleCreateExpenseFromTask}
          financeCategories={allCategories}
        />
      )}

    </div>
  );
}
